import React, { useState, useEffect, useRef } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { useSubscription } from '../../context/SubscriptionContext';
import { PulsatingButton } from '../ui/pulsating-button';

interface UpdatePaymentMethodModalProps {
  onClose: () => void;
  onSuccess?: () => void;
}

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#fff',
      fontFamily: '"Inter", sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#6B7280',
      },
    },
    invalid: {
      color: '#EF4444',
      iconColor: '#EF4444',
    },
  },
  hidePostalCode: true,
};

const UpdatePaymentMethodModal: React.FC<UpdatePaymentMethodModalProps> = ({ onClose, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { createSetupIntent, updatePaymentMethod } = useSubscription();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize SetupIntent - simplified approach
  useEffect(() => {
    let cancelled = false;

    const initializeSetupIntent = async () => {
      try {
        console.log('Creating setup intent for payment method update...');
        const { clientSecret: newClientSecret } = await createSetupIntent();
        console.log('Received client secret:', newClientSecret ? 'yes' : 'no');
        
        if (!newClientSecret) {
          throw new Error('No client secret received from setup intent');
        }

        if (!cancelled) {
          setClientSecret(newClientSecret);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Setup intent error:', err);
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to initialize payment. Please try again.');
          setIsLoading(false);
        }
      }
    };

    initializeSetupIntent();

    return () => {
      cancelled = true;
    };
  }, [createSetupIntent]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      console.error('Stripe not initialized:', { 
        stripeAvailable: !!stripe, 
        elementsAvailable: !!elements,
        stripeKey: process.env.REACT_APP_STRIPE_PUBLIC_KEY
      });
      setError('Stripe has not been properly initialized');
      return;
    }

    if (!clientSecret) {
      setError('No client secret available. Please try again.');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      console.log('Confirming card setup...');
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) throw new Error('Card element not found');

      const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (error) {
        console.error('Card setup error:', error);
        throw new Error(error.message);
      }

      if (!setupIntent?.payment_method) {
        throw new Error('Failed to setup payment method');
      }

      console.log('Card setup successful, updating payment method...');
      const paymentMethodId = typeof setupIntent.payment_method === 'string' 
        ? setupIntent.payment_method 
        : setupIntent.payment_method.id;

      const result = await updatePaymentMethod(paymentMethodId);
      
      if (result.success) {
        setSuccessMessage('Payment method updated successfully!');
        
        if (result.recovered_subscriptions.length > 0) {
          setSuccessMessage(`Payment method updated and ${result.recovered_subscriptions.length} subscription(s) recovered!`);
        }
        
        // Notify parent and close after a brief delay
        setTimeout(() => {
          if (onSuccess) {
            onSuccess();
          }
          onClose();
        }, 2000);
      } else {
        throw new Error('Failed to update payment method');
      }
      
    } catch (err) {
      console.error('Payment method update error:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-8 overflow-y-auto flex-1">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white">
              Update Payment Method
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white"
              disabled={processing}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {successMessage ? (
            <div className="mb-6 p-4 bg-green-900/50 border border-green-500 rounded-lg text-green-200 text-center">
              <svg className="w-12 h-12 text-green-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {successMessage}
            </div>
          ) : (
            <>
              <p className="text-gray-300 mb-4">
                Enter your new card details to update your payment method.
              </p>
              <p className="text-sm text-gray-400 mb-6">
                This will become your default payment method for all future charges.
              </p>

              <form onSubmit={handleSubmit}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Card Details
                  </label>
                  <div className="bg-gray-800 rounded-lg p-4">
                    <CardElement options={CARD_ELEMENT_OPTIONS} />
                  </div>
                </div>

                {error && (
                  <div className="mb-4 p-4 bg-red-900/50 border border-red-500 rounded-lg text-red-200 text-sm">
                    {error}
                  </div>
                )}

                <div className="mt-6">
                  <PulsatingButton
                    type="submit"
                    disabled={!stripe || processing || isLoading || !clientSecret}
                    style={{
                      width: '100%',
                      opacity: (!stripe || processing || isLoading || !clientSecret) ? 0.7 : 1,
                      cursor: (!stripe || processing || isLoading || !clientSecret) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {isLoading ? 'Loading...' : processing ? 'Processing...' : 'Update Payment Method'}
                  </PulsatingButton>
                </div>
              </form>

              <p className="text-gray-400 text-sm text-center mt-6">
                Your payment information is securely processed by Stripe
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpdatePaymentMethodModal;


